/**
 * Production Performance Monitoring System
 * 
 * Comprehensive performance tracking with:
 * - Core Web Vitals monitoring
 * - User interaction tracking
 * - API performance metrics
 * - Resource usage monitoring
 * - Real-time analytics
 * - Automated alerting
 */
// @ts-nocheck


export interface PerformanceMetrics {
  // Core Web Vitals
  coreWebVitals: {
    lcp: number | null; // Largest Contentful Paint
    fid: number | null; // First Input Delay
    cls: number | null; // Cumulative Layout Shift
    fcp: number | null; // First Contentful Paint
    ttfb: number | null; // Time to First Byte
  };
  
  // Navigation timing
  navigation: {
    loadTime: number;
    domContentLoaded: number;
    firstPaint: number;
    firstContentfulPaint: number;
  };
  
  // Resource metrics
  resources: {
    jsSize: number;
    cssSize: number;
    imageSize: number;
    totalSize: number;
    resourceCount: number;
  };
  
  // User interaction metrics
  interactions: {
    clickEvents: number;
    scrollEvents: number;
    keyboardEvents: number;
    formInteractions: number;
    averageTaskTime: number;
  };
  
  // API performance
  api: {
    averageResponseTime: number;
    slowQueries: number;
    errorRate: number;
    totalRequests: number;
  };
  
  // Memory usage
  memory: {
    usedHeapSize: number;
    totalHeapSize: number;
    heapSizeLimit: number;
  };
  
  // Session info
  session: {
    duration: number;
    pageViews: number;
    userAgent: string;
    timestamp: number;
  };
}

export interface AlertConfig {
  lcpThreshold: number;
  fidThreshold: number;
  clsThreshold: number;
  errorRateThreshold: number;
  responseTimeThreshold: number;
  memoryThreshold: number;
}

export interface MonitoringConfig {
  enableCoreWebVitals: boolean;
  enableUserInteractions: boolean;
  enableResourceMonitoring: boolean;
  enableMemoryMonitoring: boolean;
  sampleRate: number;
  alertConfig: AlertConfig;
  reportingEndpoint?: string;
  reportingInterval: number;
}

const DEFAULT_CONFIG: MonitoringConfig = {
  enableCoreWebVitals: true,
  enableUserInteractions: true,
  enableResourceMonitoring: true,
  enableMemoryMonitoring: true,
  sampleRate: 1.0,
  alertConfig: {
    lcpThreshold: 2500, // 2.5s
    fidThreshold: 100,  // 100ms
    clsThreshold: 0.1,  // 0.1
    errorRateThreshold: 0.05, // 5%
    responseTimeThreshold: 3000, // 3s
    memoryThreshold: 0.8 // 80% of heap limit
  },
  reportingInterval: 30000 // 30 seconds
};

export class PerformanceMonitor {
  private metrics: PerformanceMetrics;
  private config: MonitoringConfig;
  private observers: Map<string, PerformanceObserver> = new Map();
  private eventListeners: Map<string, EventListener> = new Map();
  private reportingTimer: NodeJS.Timeout | null = null;
  private sessionStartTime: number;
  private lastReportTime: number = 0;

  constructor(config: Partial<MonitoringConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.sessionStartTime = Date.now();
    
    this.metrics = this.initializeMetrics();
    this.startMonitoring();
  }

  private initializeMetrics(): PerformanceMetrics {
    return {
      coreWebVitals: {
        lcp: null,
        fid: null,
        cls: null,
        fcp: null,
        ttfb: null
      },
      navigation: {
        loadTime: 0,
        domContentLoaded: 0,
        firstPaint: 0,
        firstContentfulPaint: 0
      },
      resources: {
        jsSize: 0,
        cssSize: 0,
        imageSize: 0,
        totalSize: 0,
        resourceCount: 0
      },
      interactions: {
        clickEvents: 0,
        scrollEvents: 0,
        keyboardEvents: 0,
        formInteractions: 0,
        averageTaskTime: 0
      },
      api: {
        averageResponseTime: 0,
        slowQueries: 0,
        errorRate: 0,
        totalRequests: 0
      },
      memory: {
        usedHeapSize: 0,
        totalHeapSize: 0,
        heapSizeLimit: 0
      },
      session: {
        duration: 0,
        pageViews: 1,
        userAgent: navigator.userAgent,
        timestamp: Date.now()
      }
    };
  }

  private startMonitoring(): void {
    if (this.config.enableCoreWebVitals) {
      this.monitorCoreWebVitals();
    }
    
    if (this.config.enableUserInteractions) {
      this.monitorUserInteractions();
    }
    
    if (this.config.enableResourceMonitoring) {
      this.monitorResources();
    }
    
    if (this.config.enableMemoryMonitoring) {
      this.monitorMemoryUsage();
    }

    this.monitorNavigation();
    this.startPeriodicReporting();
  }

  private monitorCoreWebVitals(): void {
    // Largest Contentful Paint
    if ('PerformanceObserver' in window) {
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1] as PerformanceEntry & { startTime: number };
          this.metrics.coreWebVitals.lcp = lastEntry.startTime;
          this.checkAlert('lcp', lastEntry.startTime);
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
        this.observers.set('lcp', lcpObserver);
      } catch (e) {
        console.warn('LCP monitoring not supported');
      }

      // First Input Delay
      try {
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            this.metrics.coreWebVitals.fid = entry.processingStart - entry.startTime;
            this.checkAlert('fid', this.metrics.coreWebVitals.fid);
          });
        });
        fidObserver.observe({ entryTypes: ['first-input'] });
        this.observers.set('fid', fidObserver);
      } catch (e) {
        console.warn('FID monitoring not supported');
      }

      // Cumulative Layout Shift
      try {
        const clsObserver = new PerformanceObserver((list) => {
          let clsValue = 0;
          const entries = list.getEntries();
          entries.forEach((entry) => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          });
          this.metrics.coreWebVitals.cls = clsValue;
          this.checkAlert('cls', clsValue);
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] });
        this.observers.set('cls', clsObserver);
      } catch (e) {
        console.warn('CLS monitoring not supported');
      }
    }
  }

  private monitorUserInteractions(): void {
    const clickListener = () => {
      this.metrics.interactions.clickEvents++;
    };
    
    const scrollListener = () => {
      this.metrics.interactions.scrollEvents++;
    };
    
    const keyListener = () => {
      this.metrics.interactions.keyboardEvents++;
    };
    
    const formListener = () => {
      this.metrics.interactions.formInteractions++;
    };

    document.addEventListener('click', clickListener, { passive: true });
    document.addEventListener('scroll', scrollListener, { passive: true });
    document.addEventListener('keydown', keyListener, { passive: true });
    document.addEventListener('submit', formListener, { passive: true });

    this.eventListeners.set('click', clickListener);
    this.eventListeners.set('scroll', scrollListener);
    this.eventListeners.set('keydown', keyListener);
    this.eventListeners.set('submit', formListener);
  }

  private monitorResources(): void {
    if ('PerformanceObserver' in window) {
      try {
        const resourceObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            const size = entry.transferSize || 0;
            this.metrics.resources.totalSize += size;
            this.metrics.resources.resourceCount++;

            if (entry.name.includes('.js')) {
              this.metrics.resources.jsSize += size;
            } else if (entry.name.includes('.css')) {
              this.metrics.resources.cssSize += size;
            } else if (entry.name.match(/\.(png|jpg|jpeg|gif|webp|svg)$/)) {
              this.metrics.resources.imageSize += size;
            }
          });
        });
        resourceObserver.observe({ entryTypes: ['resource'] });
        this.observers.set('resource', resourceObserver);
      } catch (e) {
        console.warn('Resource monitoring not supported');
      }
    }
  }

  private monitorMemoryUsage(): void {
    const updateMemory = () => {
      if ('memory' in performance) {
        const memory = (performance as any).memory;
        this.metrics.memory = {
          usedHeapSize: memory.usedJSHeapSize,
          totalHeapSize: memory.totalJSHeapSize,
          heapSizeLimit: memory.jsHeapSizeLimit
        };

        const memoryUsageRatio = memory.usedJSHeapSize / memory.jsHeapSizeLimit;
        this.checkAlert('memory', memoryUsageRatio);
      }
    };

    updateMemory();
    setInterval(updateMemory, 5000); // Check every 5 seconds
  }

  private monitorNavigation(): void {
    if (performance.timing) {
      const timing = performance.timing;
      this.metrics.navigation = {
        loadTime: timing.loadEventEnd - timing.navigationStart,
        domContentLoaded: timing.domContentLoadedEventEnd - timing.navigationStart,
        firstPaint: 0,
        firstContentfulPaint: 0
      };
    }

    // Monitor paint events
    if ('PerformanceObserver' in window) {
      try {
        const paintObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            if (entry.name === 'first-paint') {
              this.metrics.navigation.firstPaint = entry.startTime;
            } else if (entry.name === 'first-contentful-paint') {
              this.metrics.navigation.firstContentfulPaint = entry.startTime;
              this.metrics.coreWebVitals.fcp = entry.startTime;
            }
          });
        });
        paintObserver.observe({ entryTypes: ['paint'] });
        this.observers.set('paint', paintObserver);
      } catch (e) {
        console.warn('Paint monitoring not supported');
      }
    }
  }

  public recordApiCall(responseTime: number, isError: boolean): void {
    const currentTotal = this.metrics.api.totalRequests;
    const currentAverage = this.metrics.api.averageResponseTime;
    
    this.metrics.api.totalRequests++;
    this.metrics.api.averageResponseTime = 
      (currentAverage * currentTotal + responseTime) / this.metrics.api.totalRequests;
    
    if (isError) {
      this.metrics.api.errorRate = 
        (this.metrics.api.errorRate * currentTotal + 1) / this.metrics.api.totalRequests;
    }
    
    if (responseTime > this.config.alertConfig.responseTimeThreshold) {
      this.metrics.api.slowQueries++;
    }

    this.checkAlert('errorRate', this.metrics.api.errorRate);
    this.checkAlert('responseTime', responseTime);
  }

  private checkAlert(metricType: string, value: number): void {
    const { alertConfig } = this.config;
    let shouldAlert = false;
    let alertMessage = '';

    switch (metricType) {
      case 'lcp':
        shouldAlert = value > alertConfig.lcpThreshold;
        alertMessage = `LCP is ${value}ms (threshold: ${alertConfig.lcpThreshold}ms)`;
        break;
      case 'fid':
        shouldAlert = value > alertConfig.fidThreshold;
        alertMessage = `FID is ${value}ms (threshold: ${alertConfig.fidThreshold}ms)`;
        break;
      case 'cls':
        shouldAlert = value > alertConfig.clsThreshold;
        alertMessage = `CLS is ${value} (threshold: ${alertConfig.clsThreshold})`;
        break;
      case 'errorRate':
        shouldAlert = value > alertConfig.errorRateThreshold;
        alertMessage = `Error rate is ${(value * 100).toFixed(2)}% (threshold: ${(alertConfig.errorRateThreshold * 100).toFixed(2)}%)`;
        break;
      case 'responseTime':
        shouldAlert = value > alertConfig.responseTimeThreshold;
        alertMessage = `Response time is ${value}ms (threshold: ${alertConfig.responseTimeThreshold}ms)`;
        break;
      case 'memory':
        shouldAlert = value > alertConfig.memoryThreshold;
        alertMessage = `Memory usage is ${(value * 100).toFixed(2)}% (threshold: ${(alertConfig.memoryThreshold * 100).toFixed(2)}%)`;
        break;
    }

    if (shouldAlert) {
      this.triggerAlert(metricType, alertMessage, value);
    }
  }

  private triggerAlert(metricType: string, message: string, value: number): void {
    console.warn(`Performance Alert [${metricType}]: ${message}`);
    
    // Send alert to monitoring service
    if (this.config.reportingEndpoint) {
      fetch(this.config.reportingEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'performance_alert',
          metric: metricType,
          value,
          message,
          timestamp: Date.now(),
          url: window.location.href,
          userAgent: navigator.userAgent
        })
      }).catch(() => {
        // Silently handle reporting errors
      });
    }

    // Custom event for application handling
    window.dispatchEvent(new CustomEvent('performance-alert', {
      detail: { metricType, message, value }
    }));
  }

  private startPeriodicReporting(): void {
    this.reportingTimer = setInterval(() => {
      this.updateSessionMetrics();
      this.reportMetrics();
    }, this.config.reportingInterval);
  }

  private updateSessionMetrics(): void {
    this.metrics.session.duration = Date.now() - this.sessionStartTime;
  }

  private reportMetrics(): void {
    if (Math.random() > this.config.sampleRate) {
      return; // Skip reporting based on sample rate
    }

    const now = Date.now();
    if (now - this.lastReportTime < this.config.reportingInterval) {
      return; // Don't report too frequently
    }

    this.lastReportTime = now;
    const metricsSnapshot = { ...this.metrics };

    // Send to reporting endpoint
    if (this.config.reportingEndpoint) {
      fetch(this.config.reportingEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'performance_metrics',
          metrics: metricsSnapshot,
          timestamp: now,
          url: window.location.href
        })
      }).catch(() => {
        // Silently handle reporting errors
      });
    }

    // Store locally for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log('Performance Metrics:', metricsSnapshot);
    }
  }

  public getMetrics(): PerformanceMetrics {
    this.updateSessionMetrics();
    return { ...this.metrics };
  }

  public exportMetrics(): string {
    return JSON.stringify(this.getMetrics(), null, 2);
  }

  public resetMetrics(): void {
    this.metrics = this.initializeMetrics();
    this.sessionStartTime = Date.now();
  }

  public destroy(): void {
    // Clear observers
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();

    // Remove event listeners
    this.eventListeners.forEach((listener, event) => {
      document.removeEventListener(event, listener);
    });
    this.eventListeners.clear();

    // Clear reporting timer
    if (this.reportingTimer) {
      clearInterval(this.reportingTimer);
      this.reportingTimer = null;
    }
  }
}

// Singleton instance for global usage
let globalMonitor: PerformanceMonitor | null = null;

export const initializePerformanceMonitoring = (
  config?: Partial<MonitoringConfig>
): PerformanceMonitor => {
  if (globalMonitor) {
    globalMonitor.destroy();
  }
  
  globalMonitor = new PerformanceMonitor(config);
  return globalMonitor;
};

export const getPerformanceMonitor = (): PerformanceMonitor | null => {
  return globalMonitor;
};

export const recordApiPerformance = (responseTime: number, isError: boolean = false): void => {
  if (globalMonitor) {
    globalMonitor.recordApiCall(responseTime, isError);
  }
};